/**
 * Sprint 11.3 — Resume Template Settings (Admin).
 *
 * Best-in-class settings UI for the company-wide resume template:
 *  - Split-pane layout (editor left, sticky live preview right on lg+).
 *  - Tabbed editor: Brand & Layout · Sections · Footer & Notes.
 *  - Dirty-state indicator + Reset, with confirm dialog before destructive reset.
 *  - Active version badge ("Active v{n}") next to the title.
 *  - Version-history side sheet listing past versions; click a row to read-only
 *    preview that version's sections.
 *  - Robust UX states (loading skeleton, fetch error, save error toast).
 *
 * Cache-sync hardening: after a successful save we both
 *   1) `setQueryData(['v1-resume-template'], saved)` — instant local update
 *   2) `invalidateQueries(...)` — refresh from server in background
 * so the version badge bumps reliably without race conditions.
 */
import React, { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { v1Get, v1Post } from '@/lib/api';
import { useDerivedState } from '@/hooks/use-derived-state';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Save, Palette, ListChecks, FileText, Bot,
  AlertCircle, CheckCircle2, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { CompanyResumeView } from '@/components/CompanyResumeView';
import { SAMPLE_PREVIEW } from '@/lib/resumeTemplateCatalog';
import { HEX_RE, isDirty } from '@/lib/resumeTemplateUtils';
import { ResetButton } from '@/components/resumeTemplate/ResumeTemplateSectionControls';
import { ResumeTemplateEditorTabs } from '@/components/resumeTemplate/ResumeTemplateEditorTabs';
import { VersionHistorySheet, ResumeTemplateSkeleton } from '@/components/resumeTemplate/VersionHistorySheet';

export default function ResumeTemplateSettings() {
  const qc = useQueryClient();
  const {
    data, isLoading, isError, error, refetch,
  } = useQuery({
    queryKey: ['v1-resume-template'],
    queryFn: () => v1Get('/resume-templates/active'),
  });

  const [tpl, setTpl] = useDerivedState(data);
  const dirty = useMemo(() => isDirty(tpl, data), [tpl, data]);
  const brandOk = !tpl?.brand_color || HEX_RE.test(tpl.brand_color);

  const saveMut = useMutation({
    mutationFn: async (payload) => v1Post('/resume-templates', payload),
    onSuccess: (saved) => {
      // Sprint 11.3 cache-sync hardening: seed cache with response BEFORE
      // invalidate, guaranteeing the version badge updates instantly.
      qc.setQueryData(['v1-resume-template'], saved);
      qc.invalidateQueries({ queryKey: ['v1-resume-template'] });
      qc.invalidateQueries({ queryKey: ['v1-resume-template-versions'] });
      toast.success(`Saved. Active version bumped to v${saved.version}.`);
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || e?.message || 'Failed to save template';
      toast.error(typeof detail === 'string' ? detail : 'Failed to save template');
    },
  });

  // -------- handlers --------
  const updateSection = (i, key, value) => {
    if (!tpl) return;
    const next = [...tpl.sections];
    next[i] = { ...next[i], [key]: value };
    setTpl({ ...tpl, sections: next });
  };

  const removeSection = (sectionKey) => {
    if (!tpl) return;
    setTpl({ ...tpl, sections: tpl.sections.filter((s) => s.key !== sectionKey) });
  };

  const addSection = (catalogEntry) => {
    if (!tpl) return;
    const nextOrder = (tpl.sections.reduce((m, s) => Math.max(m, Number(s.order) || 0), 0) || 0) + 1;
    const next = {
      key: catalogEntry.key,
      label: catalogEntry.label,
      enabled: true,
      order: Math.min(nextOrder, 9),
      type: catalogEntry.type,
      ...(catalogEntry.fields?.length ? { fields: catalogEntry.fields } : {}),
    };
    setTpl({ ...tpl, sections: [...tpl.sections, next] });
  };

  const onReset = () => {
    if (!data) return;
    setTpl(data);
    toast.info('Reverted unsaved changes.');
  };

  const onSave = () => {
    if (!tpl) return;
    if (!brandOk) {
      toast.error('Brand colour must be a valid hex (e.g. #0078D4).');
      return;
    }
    // Omit parser_instructions so a missing/empty FE field cannot wipe a
    // server-side override (repo inherits previous meta when the field is None).
    saveMut.mutate({
      name: tpl.name,
      brand_color: tpl.brand_color,
      show_photo: tpl.show_photo,
      show_contact_details: !!tpl.show_contact_details,
      footer_note: tpl.footer_note,
      sections: tpl.sections,
    });
  };

  // -------- render --------
  if (isLoading) return <ResumeTemplateSkeleton />;
  if (isError) {
    return (
      <div className="space-y-6" data-testid="resume-template-error">
        <PageHeader title="Resume Template" subtitle="The standard layout into which every uploaded resume is converted by AI." />
        <Card className="card-elevated">
          <CardContent className="flex items-center gap-3 p-6 text-sm">
            <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />
            <div className="flex-1">
              <p className="font-medium">Couldn&apos;t load the resume template.</p>
              <p className="text-muted-foreground">{String(error?.message || 'Unknown error')}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="resume-template-retry-button">Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!tpl) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6">
        <PageHeader
          title="Resume Template"
          subtitle="The standardised layout into which every uploaded resume is converted by AI. HR can also hand-edit each candidate's converted resume."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="secondary"
                className="gap-1 border border-primary/20 bg-primary/10 px-2.5 py-1 text-primary"
                data-testid="resume-template-active-version-badge"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Active v{tpl.version}
              </Badge>

              {dirty && (
                <Badge
                  variant="outline"
                  className="gap-1 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                  data-testid="resume-template-dirty-badge"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  Unsaved changes
                </Badge>
              )}

              <VersionHistorySheet activeVersionId={tpl.template_version_id} />

              <ResetButton disabled={!dirty || saveMut.isPending} onConfirm={onReset} />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={onSave}
                    disabled={!dirty || saveMut.isPending || !brandOk}
                    className="gap-1.5"
                    data-testid="resume-template-save-button"
                  >
                    <Save className="h-4 w-4" />
                    {saveMut.isPending ? 'Saving…' : `Save (v${tpl.version} → v${tpl.version + 1})`}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {dirty ? 'Persist edits and bump the active version.' : 'No changes to save.'}
                </TooltipContent>
              </Tooltip>
            </div>
          }
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,_460px)_minmax(0,_1fr)] xl:grid-cols-[minmax(0,_520px)_minmax(0,_1fr)]">
          {/* ============== LEFT — EDITOR ============== */}
          <Card className="card-elevated" data-testid="resume-template-editor-card">
            <CardContent className="p-0">
              <Tabs defaultValue="brand" className="w-full">
                <div className="border-b px-4 pt-3">
                  <TabsList className="grid w-full grid-cols-4" data-testid="resume-template-editor-tabs">
                    <TabsTrigger value="brand" className="gap-1.5" data-testid="resume-template-tab-brand">
                      <Palette className="h-3.5 w-3.5" /> Brand
                    </TabsTrigger>
                    <TabsTrigger value="sections" className="gap-1.5" data-testid="resume-template-tab-sections">
                      <ListChecks className="h-3.5 w-3.5" /> Sections
                    </TabsTrigger>
                    <TabsTrigger value="ai" className="gap-1.5" data-testid="resume-template-tab-ai">
                      <Bot className="h-3.5 w-3.5" /> AI Parsing
                    </TabsTrigger>
                    <TabsTrigger value="footer" className="gap-1.5" data-testid="resume-template-tab-footer">
                      <FileText className="h-3.5 w-3.5" /> Footer
                    </TabsTrigger>
                  </TabsList>
                </div>

                <ResumeTemplateEditorTabs
                  tpl={tpl}
                  setTpl={setTpl}
                  brandOk={brandOk}
                  updateSection={updateSection}
                  removeSection={removeSection}
                  addSection={addSection}
                />
              </Tabs>
            </CardContent>
          </Card>

          {/* ============== RIGHT — LIVE PREVIEW ============== */}
          <div className="lg:sticky lg:top-4 lg:self-start">
            <Card className="card-elevated" data-testid="resume-template-preview-card">
              <CardContent className="p-5">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <p className="inline-flex items-center gap-1.5 text-sm font-semibold">
                      <Eye className="h-4 w-4 text-primary" /> Live preview
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Sample candidate rendered with your current edits. Updates instantly.
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wider">Sample data</Badge>
                </div>
                <div data-testid="resume-template-preview">
                  <CompanyResumeView resume={SAMPLE_PREVIEW} template={tpl} />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
